import React from 'react';
import { useSelector } from 'react-redux';
import Skeleton from 'react-loading-skeleton';
import { includes, map } from 'lodash-es';

import { CheckBlueIcon, ContentLoader, MobileCardRowsProps, SendIcon } from '@dev/ee-components';
import { AccessLevel, Segment } from '@dev/ee-api/ee-api-models';

import { DEFAULT_PER_PAGE_COUNT } from '@/services/constants';
import useMobile from '@/services/hooks/useMobile';
import { UNSENDABLE_SEGMENTS } from '@/services/dictionaries/UnsendableSegmentRules';
import { getAccountStore } from '@/store/account/actions';
import { getSegmentsStore } from '@/store/segments/actions';
import { SingleSegmentMenu } from '../components/TableComponent';

const skeletonRow = {
    key: 'skeleton',
    id: 'all',
    name: <Skeleton />,
    rule: <Skeleton />,
    trackHistory: <Skeleton />,
    icon: <Skeleton />,
    menu: <Skeleton />,
};
const SkeletonTemplate = {
    tableTitle: <ContentLoader mode="row" />,
    rule: <ContentLoader mode="row" />,
    trackinghistory: <ContentLoader mode="row" />,
};

export const useTableRows = () => {
    const isMobile = useMobile();
    const segments = useSelector(getSegmentsStore);
    const account = useSelector(getAccountStore);
    const canModifyCampaigns = account.GeneralInfo.AccountAccessLevel.includes(AccessLevel.ModifyCampaigns);

    if (segments?.isLoadingSegments) {
        if (isMobile) {
            return map([...new Array(DEFAULT_PER_PAGE_COUNT)], (): MobileCardRowsProps => SkeletonTemplate);
        }

        const skeletonArray = Array(DEFAULT_PER_PAGE_COUNT);
        skeletonArray.fill(skeletonRow);
        return skeletonArray;
    }

    if (isMobile) {
        return map(segments?.collection, (segment): MobileCardRowsProps => {
            const isTracking = segment.IsAllContacts ? true : segment.TrackHistory;
            return {
                tableTitle: segment.Name,
                rule: segment.Rule,
                trackinghistory: isTracking ? <CheckBlueIcon /> : <div />,
                returnOnItemClick: segment as any,
            };
        });
    }

    return map(
        segments?.collection,
        (segment: Segment, index: number): Record<string, any> => ({
            key: segment.Name,
            id: segment.Name,
            IsAllContacts: segment.IsAllContacts,
            trackHistory: segment.IsAllContacts ? true : segment.TrackHistory,
            name: segment.Name,
            rule: segment.Rule,
            icon: canModifyCampaigns && !includes(UNSENDABLE_SEGMENTS, segment.Rule) && (
                <div id={`send-button-row-${index}`}>
                    <SendIcon />
                </div>
            ),
            menu: <SingleSegmentMenu segment={segment} index={index} />,
        })
    );
};
